import { useCallback, useEffect, useMemo, useState } from 'react';

import { useDataHubClient } from '../../context/DataHubClientContext.js';
import type {
  DatasetTag,
  DatasetTagsResult,
  UpsertDatasetTagsRequest,
} from '../../services/dataHubService.interface.js';
import { DataHubService } from '../../services/dataHubService.js';
import { withCorrelationId } from '../../services/usageContext.js';
import { useScenarioFactory } from '../../telemetry/hooks/useScenarioFactory/useScenarioFactory.js';
import { classifyScenarioError } from '../../telemetry/scenarioError.js';
import { ScenarioName } from '../../telemetry/scenarios.js';

export interface UseDatasetTagsResult {
  tags: DatasetTag[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
  saveTags: (request: UpsertDatasetTagsRequest) => Promise<DatasetTagsResult>;
}

export function useDatasetTags(dataSetId: string): UseDatasetTagsResult {
  const client = useDataHubClient();
  const service = useMemo(() => new DataHubService(client), [client]);
  const scenarioFactory = useScenarioFactory();

  const [tags, setTags] = useState<DatasetTag[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setIsLoading(true);
    setError(null);

    service
      .getDatasetTags(dataSetId)
      .then((result) => {
        if (cancelled) return;
        setTags(result.tags);
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load tags');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [service, dataSetId, refreshKey]);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  const saveTags = useCallback(
    async (request: UpsertDatasetTagsRequest) => {
      const scenario = scenarioFactory.newScenario(
        ScenarioName.DatalakeSaveAttributes,
      );
      try {
        const result = await service.upsertDatasetTags(dataSetId, request, {
          extras: withCorrelationId(scenario.queryId),
        });
        setTags(result.tags);
        scenario.complete('success');
        return result;
      } catch (error) {
        const classification = classifyScenarioError(error);
        scenario.complete(classification.status, classification.data);
        throw error;
      }
    },
    [service, dataSetId, scenarioFactory],
  );

  return { tags, isLoading, error, refresh, saveTags };
}
